import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { uploadOrgTemplateLibrary } from 'src/redux/slices/project/endpoints'
import { selectActiveOrganization } from 'src/redux/slices/user/selectors'
import { TextFieldOnChangeEvent } from 'src/types/event-handlers'

import {
  Button,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  TextField
} from '@material-ui/core'
import CancelIcon from '@material-ui/icons/Cancel'

type LibraryCreatorProps = {
  toggleCreatingNewLibrary: () => void
}
const LibraryCreator: React.FC<LibraryCreatorProps> = ({ toggleCreatingNewLibrary }) => {
  const activeOrganization = useSelector(selectActiveOrganization)
  const [libraryName, setLibraryName] = useState('')
  const handleLibraryName = useCallback(
    (e: TextFieldOnChangeEvent) => {
      setLibraryName(e.target.value)
    },
    [setLibraryName],
  )

  const { getRootProps, getInputProps, acceptedFiles, isDragActive } = useDropzone({
    accept: 'application/json, .json',
    maxFiles: 1,
  })

  const [trigger, { isLoading, isSuccess }] = uploadOrgTemplateLibrary.useMutation({
    fixedCacheKey: 'uploadOrgTemplateLibrary',
  })

  const create = useCallback(() => {
    if (acceptedFiles.length === 0) return
    const file = acceptedFiles[0]
    trigger({
      title: libraryName,
      file,
      public: false,
    })
  }, [acceptedFiles, libraryName, activeOrganization])

  useEffect(() => {
    if (isSuccess) toggleCreatingNewLibrary()
  }, [toggleCreatingNewLibrary, isSuccess])

  return (
    <Fade in={true} timeout={500}>
      <Grid container alignItems='center' spacing={2}>
        <Grid item>
          <TextField
            size='small'
            helperText='Enter a library name'
            label='Name'
            value={libraryName}
            onChange={handleLibraryName}
            disabled={isLoading}
          />
        </Grid>
        <Grid item>
          <div {...getRootProps()}>
            <TextField
              size='small'
              fullWidth
              label='Template Library'
              helperText='Upload a lib to use in any project'
              variant='standard'
              value={acceptedFiles?.[0]?.name ?? ''}
              disabled={isLoading}
            />
            <input {...getInputProps()} />
          </div>
        </Grid>
        <Grid item>
          <Button
            variant='outlined'
            disabled={acceptedFiles.length !== 1 || libraryName === '' || isLoading}
            onClick={create}
          >
            Create
          </Button>
        </Grid>
        <Grid item>
          <IconButton disabled={isLoading} onClick={toggleCreatingNewLibrary}>
            <CancelIcon />
          </IconButton>
        </Grid>
        {isLoading && (
          <Grid item>
            <CircularProgress />
          </Grid>
        )}
      </Grid>
    </Fade>
  )
}
export default LibraryCreator
